import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { format, differenceInDays } from "date-fns";
import { RefreshCw, AlertTriangle, Clock, CalendarDays, CheckCircle2, Users, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Get stored credentials from sessionStorage
const getAuthHeader = () => {
  const creds = sessionStorage.getItem('lyca_auth');
  if (creds) {
    return { Authorization: `Basic ${creds}` };
  }
  return {};
};

// Simple ping sound using Web Audio API
const playPingSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 880; // A5 note - pleasant ping
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.5);
  } catch (e) {
    console.log('Audio not supported');
  }
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [upcomingRenewals, setUpcomingRenewals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(false); // Sound OFF by default for management dashboard
  const hasPlayedSound = useRef(false);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = getAuthHeader();
      const [statsRes, renewalsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { headers }),
        axios.get(`${API}/dashboard/upcoming-renewals`, { headers }),
      ]);
      setStats(statsRes.data);
      setUpcomingRenewals(renewalsRes.data);
      
      // Play ping sound if should_alert is true (first load only per session)
      if (statsRes.data.should_alert && soundEnabled && !hasPlayedSound.current) {
        playPingSound();
        hasPlayedSound.current = true;
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      if (error.response?.status === 401) {
        toast.error("Session expired. Please refresh the page.");
      } else {
        toast.error("Failed to load dashboard data");
      }
    } finally {
      setLoading(false);
    }
  }, [soundEnabled]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleMarkRenewed = async (taskId, isRenewed) => {
    try {
      const headers = getAuthHeader();
      await axios.put(`${API}/renewal-tasks/${taskId}/renew`, { is_renewed: isRenewed }, { headers });
      toast.success(isRenewed ? "Marked as renewed!" : "Marked as pending");
      fetchDashboardData();
    } catch (error) {
      console.error("Error updating task:", error);
      toast.error("Failed to update task");
    }
  };

  const handleMarkBillingProcessed = async (taskId, isProcessed) => {
    try {
      const headers = getAuthHeader();
      await axios.put(`${API}/renewal-tasks/${taskId}/renew`, { is_billing_processed: isProcessed }, { headers });
      toast.success(isProcessed ? "Billing marked as processed!" : "Billing marked as pending");
      fetchDashboardData();
    } catch (error) {
      console.error("Error updating billing status:", error);
      toast.error("Failed to update billing status");
    }
  };

  const getDaysUntil = (dateStr) => {
    const renewalDate = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    renewalDate.setHours(0, 0, 0, 0);
    return differenceInDays(renewalDate, today);
  };

  const getStatusBadge = (daysUntil) => {
    if (daysUntil < 0) return { class: "badge-overdue", text: `${Math.abs(daysUntil)}d overdue` };
    if (daysUntil === 0) return { class: "badge-urgent", text: "Today" };
    if (daysUntil === 1) return { class: "badge-urgent", text: "Tomorrow" };
    if (daysUntil <= 3) return { class: "badge-warning", text: `${daysUntil} days` };
    return { class: "badge-normal", text: `${daysUntil} days` };
  };

  const toggleSound = () => {
    setSoundEnabled(!soundEnabled);
    if (!soundEnabled) {
      hasPlayedSound.current = false; // Reset so sound can play again
    }
  };

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center min-h-screen">
        <RefreshCw className="w-8 h-8 text-[#00D26A] animate-spin" />
      </div>
    );
  }

  const statCards = [
    { key: "overdue", label: "Overdue", value: stats?.overdue || 0, color: "text-red-500", bgClass: "overdue", icon: AlertTriangle },
    { key: "due_today", label: "Due Today", value: stats?.due_today || 0, color: "text-red-400", bgClass: "urgent", icon: AlertTriangle },
    { key: "due_in_1_day", label: "Due Tomorrow", value: (stats?.due_in_1_day || 0) - (stats?.due_today || 0), color: "text-orange-500", bgClass: "urgent", icon: Clock },
    { key: "due_in_2_days", label: "Due in 2 Days", value: stats?.due_in_2_days || 0, color: "text-yellow-500", bgClass: "warning", icon: Clock },
    { key: "due_in_3_days", label: "Due in 3 Days", value: stats?.due_in_3_days || 0, color: "text-blue-400", bgClass: "normal", icon: CalendarDays },
    { key: "due_in_5_days", label: "Due in 5 Days", value: stats?.due_in_5_days || 0, color: "text-slate-400", bgClass: "normal", icon: CalendarDays },
  ];

  return (
    <div className="page-container" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-white" data-testid="dashboard-title">
            Renewal Dashboard
          </h1>
          <p className="text-slate-400 mt-1">Track and manage customer plan renewals</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={toggleSound}
            className={`p-2 rounded-md transition-colors ${soundEnabled ? 'bg-[#00D26A]/20 text-[#00D26A]' : 'bg-slate-800 text-slate-500'}`}
            title={soundEnabled ? "Sound alerts enabled" : "Sound alerts disabled"}
            data-testid="sound-toggle-btn"
          >
            {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          </button>
          <button
            onClick={() => { hasPlayedSound.current = false; fetchDashboardData(); }}
            className="btn-secondary flex items-center gap-2"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Alert Banner */}
      {stats?.should_alert && (
        <div className="mb-6 p-4 rounded-md border border-red-500/30 bg-red-500/10 flex items-center gap-3" data-testid="alert-banner">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <div className="text-sm">
            <span className="font-semibold text-red-400">Attention Required: </span>
            <span className="text-slate-300">
              {stats.overdue > 0 && `${stats.overdue} overdue renewal${stats.overdue > 1 ? 's' : ''}. `}
              {stats.due_today > 0 && `${stats.due_today} renewal${stats.due_today > 1 ? 's' : ''} due today. `}
              {stats.is_friday && stats.weekend_renewals > 0 && `${stats.weekend_renewals} weekend renewal${stats.weekend_renewals > 1 ? 's' : ''} coming up.`}
            </span>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8" data-testid="stats-grid">
        {statCards.map((stat) => (
          <div
            key={stat.key}
            className={`stat-card ${stat.bgClass}`}
            data-testid={`stat-${stat.key}`}
          >
            <stat.icon className={`w-6 h-6 ${stat.color} mb-2`} />
            <div className={`stat-number ${stat.color}`}>{stat.value}</div>
            <div className="stat-label">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="card p-6" data-testid="total-customers-card">
          <div className="flex items-center gap-3">
            <Users className="w-8 h-8 text-[#00D26A]" />
            <div>
              <div className="text-3xl font-bold text-white">{stats?.total_customers || 0}</div>
              <div className="text-sm text-slate-400">Total Customers</div>
            </div>
          </div>
        </div>
        <div className="card p-6" data-testid="pending-tasks-card">
          <div className="flex items-center gap-3">
            <Clock className="w-8 h-8 text-[#0066FF]" />
            <div>
              <div className="text-3xl font-bold text-white">{stats?.total_pending_tasks || 0}</div>
              <div className="text-sm text-slate-400">Pending Tasks</div>
            </div>
          </div>
        </div>
        <div className="card p-6" data-testid="billing-cycle-card">
          <div className="flex items-center gap-3">
            <CalendarDays className="w-8 h-8 text-yellow-500" />
            <div>
              <div className="text-3xl font-bold text-white">28</div>
              <div className="text-sm text-slate-400">Day Billing Cycle</div>
            </div>
          </div>
        </div>
      </div>

      {/* Upcoming Renewals Table */}
      <div className="card" data-testid="upcoming-renewals-section">
        <div className="card-header flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white">Upcoming Renewals</h2>
          <span className="text-sm text-slate-400">Next 7 days + overdue</span>
        </div>
        <div className="card-content p-0">
          {upcomingRenewals.length === 0 ? (
            <div className="empty-state py-12">
              <CheckCircle2 className="w-12 h-12 text-[#00D26A] mb-4" />
              <p className="text-lg">All caught up!</p>
              <p className="text-sm">No pending renewals in the next 7 days.</p>
            </div>
          ) : (
            <table className="data-table" data-testid="renewals-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Customer</th>
                  <th>Mobile Number</th>
                  <th>Plan</th>
                  <th>Price</th>
                  <th>Renewal Date</th>
                  <th>Billing Processed</th>
                  <th>Plan Renewed</th>
                </tr>
              </thead>
              <tbody>
                {upcomingRenewals.map((task) => {
                  const daysUntil = getDaysUntil(task.renewal_date);
                  const status = getStatusBadge(daysUntil);
                  return (
                    <tr key={task.id} data-testid={`renewal-row-${task.id}`}>
                      <td>
                        <span className={`badge ${status.class}`}>{status.text}</span>
                      </td>
                      <td className="font-medium text-white">{task.customer_name}</td>
                      <td className="font-mono">{task.lyca_mobile_number}</td>
                      <td>{task.plan_type_name}</td>
                      <td className="font-mono">€{task.plan_price.toFixed(2)}</td>
                      <td>{format(new Date(task.renewal_date), "dd MMM yyyy")}</td>
                      <td>
                        <Checkbox
                          checked={task.is_billing_processed || false}
                          onCheckedChange={(checked) => handleMarkBillingProcessed(task.id, checked)}
                          className="w-6 h-6 border-2 border-slate-600 data-[state=checked]:bg-[#0066FF] data-[state=checked]:border-[#0066FF]"
                          data-testid={`billing-checkbox-${task.id}`}
                        />
                      </td>
                      <td>
                        <Checkbox
                          checked={task.is_renewed}
                          onCheckedChange={(checked) => handleMarkRenewed(task.id, checked)}
                          className="w-6 h-6 border-2 border-slate-600 data-[state=checked]:bg-[#00D26A] data-[state=checked]:border-[#00D26A]"
                          data-testid={`renew-checkbox-${task.id}`}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
